import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Grid,
  CircularProgress,
  Paper,
  Typography
} from '@mui/material';

/**
 * InlineEditForm component provides inline editing functionality with Save, Cancel, and Finished buttons.
 * This is a standardized editing interface for any type of data.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Current data object to edit
 * @param {Array} props.fields - Array of field configurations
 * @param {Function} props.onSave - Function to call when saving (receives updated data)
 * @param {Function} props.onCancel - Function to call when canceling
 * @param {Function} props.onFinished - Function to call when finished editing
 * @param {boolean} props.isSubmitting - Whether submission is in progress
 * @param {string} props.title - Title for the edit form
 * @returns {JSX.Element} - InlineEditForm component
 */
const InlineEditForm = ({ 
  data, 
  fields, 
  onSave, 
  onCancel, 
  onFinished, 
  isSubmitting = false,
  title = "Edit Data",
  titleId,
  dialogLayout = false,
  saveLabel = 'Save',
  showFinished = true
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  
  // Initialize form data when data changes
  useEffect(() => {
    if (data) {
      console.log('=== INLINE EDIT FORM DEBUG ===');
      console.log('Data prop received:', data);
      console.log('Fields configuration:', fields);
      
      const initialData = {};
      fields.forEach(field => {
        let value = data[field.name] || '';
        
        // Handle special data types
        if (field.type === 'json' && value) {
          try {
            const parsed = JSON.parse(value);
            value = typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
          } catch {
            // If parsing fails, use as-is
          }
        }
        
        initialData[field.name] = value;
        console.log(`Setting ${field.name} to:`, value);
      });
      
      console.log('Final initial data:', initialData);
      setFormData(initialData);
      console.log('=== END INLINE EDIT FORM DEBUG ===');
    }
  }, [data, fields]);
  
  // Validate form data whenever it changes
  useEffect(() => {
    const validate = () => {
      const newErrors = {};
      let isValid = true;
      
      fields.forEach(field => {
        if (field.required && (!formData[field.name] || String(formData[field.name]).trim() === '')) {
          newErrors[field.name] = `${field.label} is required`;
          isValid = false;
        }
        
        // Add custom validation if provided
        if (field.validate) {
          const fieldError = field.validate(formData[field.name], formData);
          if (fieldError) {
            newErrors[field.name] = fieldError;
            isValid = false;
          }
        }
      });
      
      setErrors(newErrors);
      setIsFormValid(isValid);
    };
    
    validate();
  }, [formData, fields]);
  
  // Handle form field changes
  const handleChange = (fieldName, value) => {
    console.log(`Field change: ${fieldName} = "${value}"`);
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [fieldName]: value
      };
      console.log('Updated form data:', newData);
      return newData;
    });
    
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: null
      }));
    }
  };
  
  // Handle save
  const handleSave = () => {
    console.log('=== INLINE EDIT FORM SAVE DEBUG ===');
    console.log('Form data before save:', formData);
    console.log('Form data keys:', Object.keys(formData));
    console.log('Form data values:', Object.values(formData));
    console.log('Is form valid:', isFormValid);
    console.log('Errors:', errors);
    
    // Re-validate before saving as a final check
    if (isFormValid) {
      console.log('Calling onSave with form data:', formData);
      onSave(formData);
    } else {
      console.log('Form is not valid, not calling onSave');
    }
    console.log('=== END INLINE EDIT FORM SAVE DEBUG ===');
  };
  
  // Handle cancel
  const handleCancel = () => {
    setErrors({});
    onCancel();
  };
  
  // Handle finished
  const handleFinished = () => {
    setErrors({});
    if (onFinished) onFinished();
  };
  
  return (
    <Paper
      elevation={dialogLayout ? 0 : 1}
      sx={{
        p: dialogLayout ? 0 : 3,
        mb: dialogLayout ? 0 : 2,
        display: dialogLayout ? 'flex' : 'block',
        flexDirection: dialogLayout ? 'column' : undefined,
        maxHeight: dialogLayout ? 'calc(100dvh - 32px)' : undefined,
        overflow: dialogLayout ? 'hidden' : undefined,
      }}
    >
      <Box sx={dialogLayout ? { px: 3, pt: 2.5, pb: 1.5, flexShrink: 0 } : undefined}>
        <Typography id={titleId} variant="h6" gutterBottom={!dialogLayout}>
          {title}
        </Typography>
      </Box>

      <Box sx={dialogLayout ? { px: 3, py: 1.5, overflowY: 'auto', minHeight: 0 } : undefined}>
        <Grid container spacing={2}>
          {fields.map(field => (
            <Grid item xs={12} key={field.name}>
              <TextField
                label={field.label}
                value={formData[field.name] || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                error={!!errors[field.name]}
                helperText={errors[field.name] || field.helperText || ''}
                required={field.required}
                fullWidth
                multiline={field.multiline}
                rows={field.rows || 1}
                disabled={field.disabled || isSubmitting}
                type={field.type === 'password' ? 'password' : field.type === 'number' ? 'number' : 'text'}
                inputProps={field.type === 'number' ? {
                  step: 'any',
                  min: field.name.includes('Latitude') ? -90 : field.name.includes('Longitude') ? -180 : undefined,
                  max: field.name.includes('Latitude') ? 90 : field.name.includes('Longitude') ? 180 : undefined
                } : undefined}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* The dialog variant keeps actions visible while the fields scroll. */}
      <Box sx={{
        mt: dialogLayout ? 0 : 3,
        px: dialogLayout ? 3 : 0,
        py: dialogLayout ? 2 : 0,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1,
        flexShrink: 0,
        borderTop: dialogLayout ? 1 : 0,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}>
        <Button 
          onClick={handleCancel} 
          disabled={isSubmitting}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          disabled={isSubmitting || !isFormValid}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            saveLabel
          )}
        </Button>
        {showFinished && (
          <Button
            onClick={handleFinished}
            variant="contained"
            color="secondary"
            disabled={isSubmitting}
          >
            Finished
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default InlineEditForm;
